/**
 * Pure geometry utility functions for flight planning calculations.
 * All functions are stateless and independently testable.
 */

import { LAT_TO_METERS, lngToMetersAtLat } from './coordinateUtils';

/**
 * Ray-casting point-in-polygon test.
 * @param {{ lat: number, lng: number }} point
 * @param {{ lat: number, lng: number }[]} polygon
 * @returns {boolean}
 */
export const isPointInPolygon = (point, polygon) => {
	let inside = false;
	const n = polygon.length;
	for (let i = 0, j = n - 1; i < n; j = i++) {
		const xi = polygon[i].lng,
			yi = polygon[i].lat;
		const xj = polygon[j].lng,
			yj = polygon[j].lat;
		if (
			yi > point.lat !== yj > point.lat &&
			point.lng < ((xj - xi) * (point.lat - yi)) / (yj - yi) + xi
		) {
			inside = !inside;
		}
	}
	return inside;
};

/**
 * Find intersection point of two line segments.
 * Returns null if segments don't intersect.
 * @param {{ lat: number, lng: number }} p1 - Segment 1 start
 * @param {{ lat: number, lng: number }} p2 - Segment 1 end
 * @param {{ lat: number, lng: number }} p3 - Segment 2 start
 * @param {{ lat: number, lng: number }} p4 - Segment 2 end
 * @returns {{ lat: number, lng: number, t: number } | null}
 */
export const lineIntersection = (p1, p2, p3, p4) => {
	const denom = (p4.lat - p3.lat) * (p2.lng - p1.lng) - (p4.lng - p3.lng) * (p2.lat - p1.lat);
	if (Math.abs(denom) < 1e-10) return null;

	const ua =
		((p4.lng - p3.lng) * (p1.lat - p3.lat) - (p4.lat - p3.lat) * (p1.lng - p3.lng)) / denom;
	const ub =
		((p2.lng - p1.lng) * (p1.lat - p3.lat) - (p2.lat - p1.lat) * (p1.lng - p3.lng)) / denom;

	if (ua >= 0 && ua <= 1 && ub >= 0 && ub <= 1) {
		return {
			lat: p1.lat + ua * (p2.lat - p1.lat),
			lng: p1.lng + ua * (p2.lng - p1.lng),
			t: ua,
		};
	}
	return null;
};

/**
 * Clip a line segment to polygon boundaries.
 * Returns array of line segments that fall inside the polygon.
 * @param {{ lat: number, lng: number }} lineStart
 * @param {{ lat: number, lng: number }} lineEnd
 * @param {{ lat: number, lng: number }[]} polygon
 * @returns {Array<[{ lat: number, lng: number }, { lat: number, lng: number }]>}
 */
export const clipLineToPolygon = (lineStart, lineEnd, polygon) => {
	const intersections = [];
	const n = polygon.length;

	for (let i = 0; i < n; i++) {
		const j = (i + 1) % n;
		const intersection = lineIntersection(lineStart, lineEnd, polygon[i], polygon[j]);
		if (intersection) {
			intersections.push(intersection);
		}
	}

	intersections.sort((a, b) => a.t - b.t);

	const segments = [];
	const allPoints = [{ ...lineStart, t: 0 }, ...intersections, { ...lineEnd, t: 1 }];

	for (let i = 0; i < allPoints.length - 1; i++) {
		const midPoint = {
			lat: (allPoints[i].lat + allPoints[i + 1].lat) / 2,
			lng: (allPoints[i].lng + allPoints[i + 1].lng) / 2,
		};

		if (isPointInPolygon(midPoint, polygon)) {
			segments.push([
				{ lat: allPoints[i].lat, lng: allPoints[i].lng },
				{ lat: allPoints[i + 1].lat, lng: allPoints[i + 1].lng },
			]);
		}
	}

	return segments;
};

/**
 * Calculate approximate polygon area using the shoelace formula.
 * @param {{ lat: number, lng: number }[]} polygonPoints
 * @returns {number} Area in square meters
 */
export const calculatePolygonArea = polygonPoints => {
	if (polygonPoints.length < 3) return 0;

	let area = 0;
	const n = polygonPoints.length;

	for (let i = 0; i < n; i++) {
		const j = (i + 1) % n;
		const x1 = polygonPoints[i].lng * lngToMetersAtLat(polygonPoints[i].lat);
		const y1 = polygonPoints[i].lat * LAT_TO_METERS;
		const x2 = polygonPoints[j].lng * lngToMetersAtLat(polygonPoints[j].lat);
		const y2 = polygonPoints[j].lat * LAT_TO_METERS;

		area += x1 * y2 - x2 * y1;
	}

	return Math.abs(area / 2);
};

/**
 * Format area value as human-readable string.
 * @param {number} areaM2 - Area in square meters
 * @returns {string} Formatted area (e.g. "1234 m²" or "5.67 ha")
 */
export const formatArea = areaM2 => {
	if (areaM2 < 10000) {
		return `${areaM2.toFixed(0)} m²`;
	}
	return `${(areaM2 / 10000).toFixed(2)} ha`;
};
